import React from 'react'
import { useForm, Link, usePage, router } from '@inertiajs/react'
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
} from '@mui/material'
import { Delete as DeleteIcon } from '@mui/icons-material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import type { AccountUser, Account } from '@/types'

interface Props {
  member: AccountUser
}

export default function AccountUsersEdit({ member }: Props) {
  const { currentAccount } = usePage<{ currentAccount: Account }>().props
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)

  const { data, setData, patch, processing } = useForm({
    role: member.role || 'member',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/accounts/${currentAccount.slug}/members/${member.id}`)
  }

  const handleDelete = () => {
    router.delete(`/accounts/${currentAccount.slug}/members/${member.id}`)
  }

  return (
    <ManagementLayout>
      <Box sx={{ maxWidth: 672, mx: 'auto' }}>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
            {member.user.email[0].toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Edit Team Member
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {member.user.email}
            </Typography>
          </Box>
        </Box>

        <Paper sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={data.role}
                label="Role"
                onChange={(e) => setData('role', e.target.value as 'admin' | 'member')}
              >
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 3 }}>
              <Typography variant="body2" fontWeight={500}>Role Permissions:</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Admin:</strong> Full access to manage backends, credentials, virtual buckets, and team members.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                <strong>Member:</strong> Can view all resources but cannot make changes.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Button
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Remove Member
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button component={Link} href={`/accounts/${currentAccount.slug}/members`}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={processing}>
                  Update Member
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Remove Team Member"
        message={`Are you sure you want to remove ${member.user.email} from this account? They will lose all access.`}
        confirmText="Remove"
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </ManagementLayout>
  )
}
